// Console theme (light / dark) — one preference for the whole product, shared
// with the homepage's switch (src/Pages/Homepage.jsx reads the same key).
//
//   localStorage['nexsav-theme'] = 'dark' (default) | 'light'
//
// Contract: docs/light-mode.md §2.
// - getTheme() / setTheme(next) read and write the preference. setTheme also
//   announces the change in this tab with a `nexsav-theme` window event; other
//   tabs hear it through the browser's `storage` event.
// - useConsoleTheme() → { theme, isLight, toggle, setTheme }. While at least
//   one component using it is mounted, <html> carries data-theme="<theme>" and
//   the class `console-mounted`; both are removed when the last one unmounts,
//   so the marketing pages are left exactly as they were.
// - public/index.html sets data-theme before first paint (no flash); the
//   light styles live in src/styles/theme-light.*.css under
//   `html[data-theme="light"] .portal-skin`.

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'nexsav-theme';
export const THEME_EVENT = 'nexsav-theme';
export const DEFAULT_THEME = 'dark';
export const CONSOLE_MOUNTED_CLASS = 'console-mounted';

// Browser-chrome colour (mobile address bar) per theme. Dark matches the
// static <meta name="theme-color"> in public/index.html.
const THEME_COLORS = { dark: '#050507', light: '#F2EFE9' };

const hasWindow = () => typeof window !== 'undefined';
const hasDocument = () => typeof document !== 'undefined' && !!document.documentElement;

export function normalizeTheme(value) {
  return value === 'light' ? 'light' : DEFAULT_THEME;
}

export function getTheme() {
  if (!hasWindow()) return DEFAULT_THEME;
  try {
    return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

export function setTheme(next) {
  const theme = normalizeTheme(next);
  if (!hasWindow()) return theme;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage blocked (private mode, quota): the theme still applies for this
    // page view through the event below.
  }
  try {
    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { theme } }));
  } catch {
    // Very old browsers without CustomEvent: nothing else to do.
  }
  return theme;
}

// ── <html> side effects, reference-counted ──────────────────────────────────
// Layouts and any component that needs the theme (charts, the toggle) all use
// the hook; only the last one to unmount clears the attributes.
let mountedCount = 0;
let originalThemeColor = null;

function themeColorMeta() {
  return hasDocument() ? document.querySelector('meta[name="theme-color"]') : null;
}

function applyToDocument(theme) {
  if (!hasDocument()) return;
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.classList.add(CONSOLE_MOUNTED_CLASS);
  const meta = themeColorMeta();
  if (meta) {
    if (originalThemeColor === null) originalThemeColor = meta.getAttribute('content') || THEME_COLORS.dark;
    meta.setAttribute('content', theme === 'light' ? THEME_COLORS.light : originalThemeColor);
  }
}

function clearDocument() {
  if (!hasDocument()) return;
  const root = document.documentElement;
  delete root.dataset.theme;
  root.classList.remove(CONSOLE_MOUNTED_CLASS);
  const meta = themeColorMeta();
  if (meta && originalThemeColor !== null) meta.setAttribute('content', originalThemeColor);
  originalThemeColor = null;
}

// Layout effect in the browser so <html> is updated before the console's first
// paint; plain effect where there is no DOM (server render) to avoid warnings.
const useIsomorphicLayoutEffect = hasWindow() ? useLayoutEffect : useEffect;

export function useConsoleTheme() {
  const [theme, setThemeState] = useState(getTheme);

  // Follow changes from this tab (custom event) and from other tabs (storage).
  useEffect(() => {
    if (!hasWindow()) return undefined;
    const sync = () => setThemeState(getTheme());
    const onStorage = (e) => {
      // key === null means localStorage.clear() in another tab.
      if (e.key === null || e.key === THEME_STORAGE_KEY) sync();
    };
    window.addEventListener(THEME_EVENT, sync);
    window.addEventListener('storage', onStorage);
    // The preference may have changed between the first render and now.
    sync();
    return () => {
      window.removeEventListener(THEME_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    mountedCount += 1;
    return () => {
      mountedCount -= 1;
      if (mountedCount <= 0) {
        mountedCount = 0;
        clearDocument();
      }
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    applyToDocument(theme);
  }, [theme]);

  const set = useCallback((next) => setThemeState(setTheme(next)), []);
  const toggle = useCallback(() => set(theme === 'light' ? 'dark' : 'light'), [theme, set]);

  return { theme, isLight: theme === 'light', toggle, setTheme: set };
}

// Test-only: forget the reference count between isolated test cases.
export function __resetConsoleThemeForTests() {
  mountedCount = 0;
  clearDocument();
}
